/**
 * Page backoffice — Messagerie interne.
 *
 * Vue pour surveillance / direction / scolarité : recevoir et répondre aux
 * messages envoyés par les formateurs (et autres rôles internes).
 * Réutilise les mêmes endpoints que la PWA formateur.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  MessagesSquare, Send, Loader2, ChevronLeft, Plus, X, Building2, Shield,
  ClipboardList, Search, Briefcase,
} from 'lucide-react';

type DestRole = 'surveillance' | 'direction' | 'scolarite' | 'formateur';

const DEST_META: Record<string, { label: string; Icon: any; color: string; bg: string; text: string }> = {
  surveillance: { label: 'Surveillance', Icon: ClipboardList, color: 'amber',
                  bg: 'bg-amber-100 dark:bg-amber-900/30',  text: 'text-amber-700 dark:text-amber-300' },
  direction:    { label: 'Direction',    Icon: Building2,    color: 'red',
                  bg: 'bg-red-100 dark:bg-red-900/30',      text: 'text-red-700 dark:text-red-300' },
  scolarite:    { label: 'Scolarité',    Icon: Shield,       color: 'blue',
                  bg: 'bg-blue-100 dark:bg-blue-900/30',    text: 'text-blue-700 dark:text-blue-300' },
  formateur:    { label: 'Formateur',    Icon: Briefcase,    color: 'emerald',
                  bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-300' },
};

interface ThreadItem {
  thread_id: number;
  sujet: string;
  dernier_message: string;
  dernier_date: string | null;
  nb_messages: number;
  nb_non_lus: number;
  autres_participants: string[];
  destinataire_role?: string;
  expediteur_role?: string;
}

interface MessageItem {
  id: number;
  thread_id: number;
  sujet: string;
  contenu: string;
  expediteur_user_id: number;
  expediteur_nom: string;
  expediteur_role: string;
  destinataire_role: string;
  destinataire_nom: string;
  date_envoi: string;
  lu: boolean;
  a_moi: boolean;
}

function getToken(): string {
  return localStorage.getItem('auth_token') || localStorage.getItem('access_token') || '';
}

async function apiFetch(url: string, options: RequestInit = {}): Promise<any> {
  const res = await fetch(url, {
    ...options,
    headers: {
      ...(options.headers || {}),
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${getToken()}`,
    },
  });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try { detail = (await res.json()).detail || detail; } catch { /* */ }
    throw new Error(detail);
  }
  return res.json();
}

export default function MessagerieInterneAdmin() {
  const [threads, setThreads] = useState<ThreadItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtreRole, setFiltreRole] = useState<string>('');
  const [threadOuvert, setThreadOuvert] = useState<number | null>(null);
  const [showNouveau, setShowNouveau] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiFetch('/api/portail-formateur/messagerie-interne/');
      setThreads(r.results || []);
    } catch (e: any) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtres = useMemo(() => {
    const q = search.trim().toLowerCase();
    return threads.filter(t => {
      if (filtreRole && t.destinataire_role !== filtreRole) return false;
      if (q && !t.sujet.toLowerCase().includes(q)
            && !t.dernier_message.toLowerCase().includes(q)
            && !t.autres_participants.join(' ').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [threads, search, filtreRole]);

  // Groupage par destinataire_role pour visualisation
  const groupes = useMemo(() => {
    const map: Record<string, ThreadItem[]> = {};
    for (const t of filtres) {
      const k = t.destinataire_role || 'autre';
      (map[k] ||= []).push(t);
    }
    return ['surveillance', 'direction', 'scolarite', 'formateur', 'autre']
      .filter(k => map[k]?.length)
      .map(k => ({ key: k, threads: map[k] }));
  }, [filtres]);

  if (threadOuvert !== null) {
    return <VueThread threadId={threadOuvert}
      onBack={() => { setThreadOuvert(null); load(); }} />;
  }

  const totalNonLus = threads.reduce((a, t) => a + t.nb_non_lus, 0);

  return (
    <div className="max-w-5xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
      <header className="mb-4 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <MessagesSquare size={22} className="text-blue-600" /> Messagerie interne
            {totalNonLus > 0 && (
              <span className="text-[10px] font-bold bg-red-500 text-white rounded-full px-2 py-0.5">
                {totalNonLus} non lu{totalNonLus > 1 ? 's' : ''}
              </span>
            )}
          </h1>
          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
            Conversations avec les formateurs et entre services (surveillance, direction, scolarité).
          </p>
        </div>
        <button onClick={() => setShowNouveau(true)}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-semibold inline-flex items-center gap-1.5">
          <Plus size={14} /> Nouveau message
        </button>
      </header>

      {/* Filtres */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 mb-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div className="relative">
          <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher dans les conversations…"
            className="w-full pl-7 pr-2 py-1.5 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100" />
        </div>
        <select value={filtreRole} onChange={(e) => setFiltreRole(e.target.value)}
          className="w-full px-2 py-1.5 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100">
          <option value="">Tous les destinataires</option>
          <option value="surveillance">Surveillance</option>
          <option value="direction">Direction</option>
          <option value="scolarite">Scolarité</option>
          <option value="formateur">Formateur (individuel)</option>
        </select>
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-500">
          <Loader2 size={20} className="inline animate-spin" />
        </div>
      ) : groupes.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-10 text-center text-gray-500">
          <MessagesSquare size={36} className="inline mb-2 text-gray-300 dark:text-gray-600" />
          <div className="text-sm">Aucune conversation pour l'instant.</div>
          <div className="text-xs mt-1 text-gray-400">
            Les messages envoyés par les formateurs vers votre service apparaîtront ici.
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          {groupes.map(g => {
            const meta = DEST_META[g.key] || DEST_META.formateur;
            const nonLus = g.threads.reduce((a, t) => a + t.nb_non_lus, 0);
            return (
              <section key={g.key}>
                <header className="flex items-center gap-2 mb-2 px-1">
                  <meta.Icon size={14} className={meta.text} />
                  <h2 className={`text-xs font-bold uppercase tracking-wider ${meta.text}`}>
                    {meta.label}
                  </h2>
                  <span className="text-[11px] text-gray-500">
                    {g.threads.length} conversation{g.threads.length > 1 ? 's' : ''}
                  </span>
                  {nonLus > 0 && (
                    <span className="text-[10px] font-bold bg-red-500 text-white rounded-full px-1.5 py-0.5">
                      {nonLus}
                    </span>
                  )}
                </header>
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden divide-y divide-gray-100 dark:divide-gray-700">
                  {g.threads.map(t => (
                    <button key={t.thread_id} onClick={() => setThreadOuvert(t.thread_id)}
                      className="w-full p-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700/40 transition">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                            {t.sujet}
                          </div>
                          <div className="text-[10px] text-gray-500 truncate mt-0.5">
                            {t.autres_participants.join(', ') || '—'}
                          </div>
                        </div>
                        {t.nb_non_lus > 0 && (
                          <span className="text-[9px] font-bold bg-red-500 text-white rounded-full px-1.5 py-0.5 flex-shrink-0">
                            {t.nb_non_lus}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">
                        {t.dernier_message}
                      </div>
                      <div className="text-[10px] text-gray-500 mt-1 flex items-center gap-2">
                        <span>{t.nb_messages} message{t.nb_messages > 1 ? 's' : ''}</span>
                        {t.dernier_date && (
                          <span>· {new Date(t.dernier_date).toLocaleDateString('fr-FR', {
                            day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                          })}</span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {showNouveau && (
        <ModalNouveau onClose={() => setShowNouveau(false)}
          onSent={(tid) => { setShowNouveau(false); setThreadOuvert(tid); load(); }} />
      )}
    </div>
  );
}

function VueThread({ threadId, onBack }: { threadId: number; onBack: () => void }) {
  const [messages, setMessages] = useState<MessageItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [contenu, setContenu] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiFetch(`/api/portail-formateur/messagerie-interne/thread/${threadId}/`);
      setMessages(r.results || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [threadId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const premier = messages[0];
  const sujet = premier?.sujet || '(Sans sujet)';
  const destRole = premier?.destinataire_role || '';
  const meta = DEST_META[destRole] || DEST_META.formateur;

  const handle = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contenu.trim() || !premier) return;
    setSending(true);
    try {
      // Réplique : on envoie au même groupe que celui ciblé à l'origine
      await apiFetch('/api/portail-formateur/messagerie-interne/', {
        method: 'POST',
        body: JSON.stringify({
          destinataire_role: premier.destinataire_role,
          contenu,
          thread_id: threadId,
        }),
      });
      setContenu('');
      load();
    } catch (e: any) {
      alert(e.message || 'Erreur envoi');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
      <button onClick={onBack}
        className="mb-3 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 inline-flex items-center gap-1">
        <ChevronLeft size={14} /> Retour
      </button>

      <header className="mb-4">
        <div className="flex items-center gap-2 mb-1">
          <meta.Icon size={16} className={meta.text} />
          <span className={`text-[11px] font-bold uppercase tracking-wider ${meta.text}`}>
            {meta.label}
          </span>
        </div>
        <h1 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">{sujet}</h1>
      </header>

      <div ref={scrollRef}
        className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 mb-3 space-y-2 max-h-[60vh] overflow-y-auto">
        {loading && (
          <div className="text-center py-4 text-gray-500 text-sm">
            <Loader2 size={14} className="inline animate-spin mr-2" />
          </div>
        )}
        {messages.map(m => (
          <div key={m.id} className={`flex ${m.a_moi ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] rounded-lg p-2.5 ${
              m.a_moi
                ? 'bg-blue-100 dark:bg-blue-900/40 border border-blue-200 dark:border-blue-800/60'
                : 'bg-gray-100 dark:bg-gray-700/60 border border-gray-200 dark:border-gray-700'
            }`}>
              {!m.a_moi && (
                <div className="text-[10px] text-gray-500 dark:text-gray-400 mb-0.5 font-semibold">
                  {m.expediteur_nom} <span className="opacity-70">({m.expediteur_role})</span>
                </div>
              )}
              <div className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">{m.contenu}</div>
              <div className="text-[9px] mt-1 opacity-60 text-gray-700 dark:text-gray-300">
                {new Date(m.date_envoi).toLocaleString('fr-FR', {
                  day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                })}
              </div>
            </div>
          </div>
        ))}
      </div>

      <form onSubmit={handle} className="flex gap-2">
        <input value={contenu} onChange={(e) => setContenu(e.target.value)}
          placeholder="Réponse…" maxLength={5000}
          className="flex-1 px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100" />
        <button type="submit" disabled={sending || !contenu.trim()}
          className="px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded font-semibold inline-flex items-center gap-1.5">
          {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          Envoyer
        </button>
      </form>
    </div>
  );
}

function ModalNouveau({ onClose, onSent }: {
  onClose: () => void; onSent: (threadId: number) => void;
}) {
  const [dest, setDest] = useState<DestRole | null>(null);
  const [sujet, setSujet] = useState('');
  const [contenu, setContenu] = useState('');
  const [loading, setLoading] = useState(false);
  const [erreur, setErreur] = useState('');

  const handle = async (e: React.FormEvent) => {
    e.preventDefault();
    setErreur('');
    if (!dest) { setErreur('Destinataire requis'); return; }
    if (!contenu.trim()) { setErreur('Message requis'); return; }
    setLoading(true);
    try {
      const r = await apiFetch('/api/portail-formateur/messagerie-interne/', {
        method: 'POST',
        body: JSON.stringify({ destinataire_role: dest, sujet, contenu }),
      });
      onSent(r.thread_id);
    } catch (e: any) {
      setErreur(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}>
      <form onSubmit={handle} onClick={(e) => e.stopPropagation()}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-700">
        <header className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">Nouveau message</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </header>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Destinataire *</label>
            <div className="grid grid-cols-3 gap-1.5">
              {(['surveillance', 'direction', 'scolarite'] as DestRole[]).map(d => {
                const meta = DEST_META[d];
                return (
                  <button key={d} type="button" onClick={() => setDest(d)}
                    className={`px-2 py-2 text-xs rounded border inline-flex flex-col items-center gap-0.5 ${
                      dest === d
                        ? 'bg-blue-600 border-blue-600 text-white'
                        : 'bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                    }`}>
                    <meta.Icon size={14} />
                    {meta.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Sujet</label>
            <input value={sujet} onChange={(e) => setSujet(e.target.value)} maxLength={200}
              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Message *</label>
            <textarea value={contenu} onChange={(e) => setContenu(e.target.value)}
              rows={5} maxLength={5000} required
              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100 resize-none" />
          </div>
          {erreur && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded p-2 text-sm">
              {erreur}
            </div>
          )}
        </div>
        <footer className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button type="button" onClick={onClose}
            className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded">
            Annuler
          </button>
          <button type="submit" disabled={loading || !contenu.trim() || !dest}
            className="px-4 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold rounded inline-flex items-center gap-1.5">
            {loading ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
            Envoyer
          </button>
        </footer>
      </form>
    </div>
  );
}
